import type { BannerConfig, Choice, Purpose, Regime } from './types.js';

/**
 * 배너 화면.
 *
 * ## Shadow DOM 을 쓴다
 *
 * 배너는 **남의 사이트 안에서** 돈다. 그 사이트의 CSS 가 배너를 깨뜨릴 수도,
 * 배너의 CSS 가 그 사이트를 깨뜨릴 수도 있다. 둘 다 일어나면 안 된다 —
 * 특히 전자는 **동의 버튼이 안 보이는** 형태로 나타날 수 있고, 그러면
 * 이용자가 선택을 못 한다.
 *
 * ## 외부 자원을 하나도 쓰지 않는다
 *
 * 폰트·아이콘·이미지를 외부에서 불러오지 않는다. **동의 전에 제3자로
 * 나가는 것을 막는 물건이 스스로 그러면 안 된다.** 글꼴은 시스템 글꼴을
 * 쓰고 아이콘은 쓰지 않는다.
 */

interface Strings {
  readonly title: string;
  readonly body: string;
  readonly acceptAll: string;
  readonly rejectAll: string;
  readonly customize: string;
  readonly save: string;
  readonly policy: string;
  readonly essential: string;
  readonly prefs: string;
  readonly ccpaNote: string;
}

const T: Readonly<Record<string, Strings>> = {
  ko: {
    title: '쿠키 사용에 대한 선택',
    body: '이 사이트는 서비스 제공에 필요한 쿠키와, 허용하신 목적의 쿠키를 사용합니다.',
    acceptAll: '모두 허용',
    rejectAll: '모두 거부',
    customize: '항목별 선택',
    save: '선택 저장',
    policy: '쿠키 정책',
    essential: '항상 켜짐',
    prefs: '쿠키 설정',
    ccpaNote: '개인정보 판매·공유를 거부하실 수 있습니다.',
  },
  en: {
    title: 'Your cookie choices',
    body: 'This site uses cookies required for the service, plus any you allow below.',
    acceptAll: 'Accept all',
    rejectAll: 'Reject all',
    customize: 'Customize',
    save: 'Save choices',
    policy: 'Cookie policy',
    essential: 'Always on',
    prefs: 'Cookie settings',
    ccpaNote: 'You may opt out of the sale or sharing of personal information.',
  },
};

/**
 * 실제로 그릴 언어. 없는 언어는 영어로 떨어진다 — **빈 화면을 내보내지 않는
 * 것이 요점이다.**
 *
 * 문안 해시(`notice.ts`)도 이 값으로 만든다. 화면과 등록 문구가 같은 언어를
 * 가리켜야 「본 문장」이 성립한다.
 */
export function effectiveLang(lang: string): string {
  return T[lang] !== undefined ? lang : 'en';
}

export function strings(lang: string): Strings {
  return T[effectiveLang(lang)] ?? ({} as Strings);
}

export function labelOf(p: Purpose, lang: string): string {
  return p.label[lang] ?? p.label['en'] ?? p.id;
}

const CSS = [
  ':host { all: initial; }',
  '.wrap {',
  '  position: fixed; inset: auto 0 0 0; z-index: 2147483647;',
  '  font-family: system-ui, -apple-system, "Segoe UI", sans-serif;',
  '  font-size: 16px; line-height: 1.5; color: #111;',
  '  background: #fff; border-top: 1px solid #d4d4d4;',
  '  box-shadow: 0 -2px 16px rgba(0,0,0,.12);',
  '  padding: 20px; box-sizing: border-box;',
  '  max-height: 85vh; overflow-y: auto;',
  '}',
  '.inner { max-width: 880px; margin: 0 auto; }',
  'h2 { margin: 0 0 8px; font-size: 18px; font-weight: 600; }',
  'p { margin: 0 0 16px; }',
  '.row { display: flex; flex-wrap: wrap; gap: 8px; }',
  'button {',
  '  font: inherit; padding: 10px 18px; border-radius: 6px; cursor: pointer;',
  '  border: 1px solid #111; background: #111; color: #fff;',
  '}',
  'button.ghost { background: #fff; color: #111; }',
  'button:focus-visible { outline: 3px solid #2563eb; outline-offset: 2px; }',
  '.purposes { margin: 4px 0 16px; padding: 0; list-style: none; }',
  '.purposes li { padding: 10px 0; border-bottom: 1px solid #eee; }',
  '.purposes label { display: flex; gap: 10px; align-items: flex-start; cursor: pointer; }',
  '.purposes input { margin-top: 4px; width: 18px; height: 18px; }',
  '.fixed { color: #666; font-size: 14px; }',
  'a { color: #1d4ed8; }',
  // 글자 대신 아이콘 — 글자 버튼은 모바일에서 사이트의 주요 버튼을 가렸다.
  // 44px 은 터치 대상 최소 크기다 (WCAG 2.5.5).
  '.fab {',
  '  position: fixed; bottom: 16px; right: 16px; z-index: 2147483646;',
  '  width: 44px; height: 44px; padding: 0; border-radius: 50%;',
  '  display: flex; align-items: center; justify-content: center;',
  '  box-shadow: 0 2px 8px rgba(0,0,0,.18);',
  '}',
  '.fab svg { width: 24px; height: 24px; fill: currentColor; }',
  '@media (prefers-color-scheme: dark) {',
  '  .wrap { background: #18181b; color: #f4f4f5; border-top-color: #3f3f46; }',
  '  button { background: #f4f4f5; color: #18181b; border-color: #f4f4f5; }',
  '  button.ghost { background: #18181b; color: #f4f4f5; }',
  '  .purposes li { border-bottom-color: #3f3f46; }',
  '  a { color: #93c5fd; }',
  '}',
].join('\n');

const SVG_NS = 'http://www.w3.org/2000/svg';

/** 한 입 베어 문 쿠키. 장식이라 낭독기에서 숨긴다 — 이름은 버튼이 갖는다. */
function cookieIcon(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('fill-rule', 'evenodd');
  path.setAttribute(
    'd',
    'M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5Z' +
      'M8.5 8a1.5 1.5 0 1 0 0 .01ZM7 13.5a1.5 1.5 0 1 0 0 .01Z' +
      'M12 17a1.5 1.5 0 1 0 0 .01ZM15.5 13a1.5 1.5 0 1 0 0 .01ZM11.5 11a1 1 0 1 0 0 .01Z',
  );
  svg.append(path);
  return svg;
}

export interface UiHandlers {
  readonly onDecide: (picks: Record<string, Choice>) => void;
}

export class BannerUi {
  readonly #host: HTMLElement;
  readonly #root: ShadowRoot;
  readonly #config: BannerConfig;
  readonly #lang: string;
  /** 목적 이름·설명을 그리는 언어. `notice.ts` 와 같은 값이어야 한다. */
  readonly #locale: string;
  readonly #s: Strings;
  readonly #handlers: UiHandlers;
  #detail = false;

  constructor(config: BannerConfig, lang: string, handlers: UiHandlers) {
    this.#config = config;
    this.#lang = lang;
    this.#locale = effectiveLang(lang);
    this.#s = strings(lang);
    this.#handlers = handlers;
    this.#host = document.createElement('div');
    this.#host.setAttribute('data-ray-cookie-banner', '');
    this.#root = this.#host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = CSS;
    this.#root.append(style);
    this.#mount();
  }

  /**
   * 화면을 문서에 붙인다.
   *
   * 🔴 **`<head>` 에서 돌면 `document.body` 가 아직 없다.** 사전 차단은
   * 그 자리에서 즉시 걸려야 하므로 배너도 거기서 만들어지는데, 그때
   * `body.append` 를 부르면 터진다. 실제로 그렇게 터져서 배너가 아예
   * 안 떴다 — 차단은 되는데 물어보지를 않는 상태였다.
   *
   * **차단은 즉시, 화면은 body 를 기다린다.**
   */
  #mount(): void {
    if (document.body !== null) {
      document.body.append(this.#host);
      return;
    }
    document.addEventListener(
      'DOMContentLoaded',
      () => document.body.append(this.#host),
      { once: true },
    );
  }

  /** 배너를 띄운다. */
  show(regime: Regime): void {
    this.#clear();
    const wrap = document.createElement('div');
    wrap.className = 'wrap';
    // **대화상자로 알린다.** 보조기술 이용자가 「무엇이 떴는지」 알아야
    // 선택할 수 있다. 쿠키 배너는 선택을 요구하는 화면이라 더 그렇다.
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-label', this.#s.title);

    const inner = document.createElement('div');
    inner.className = 'inner';

    const h = document.createElement('h2');
    h.textContent = this.#s.title;
    const p = document.createElement('p');
    p.textContent =
      regime === 'ccpa' ? `${this.#s.body} ${this.#s.ccpaNote}` : this.#s.body;
    inner.append(h, p);

    const url = this.#config.policyUrl[this.#lang] ?? Object.values(this.#config.policyUrl)[0];
    if (url !== undefined) {
      const holder = document.createElement('p');
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      // `noopener` 가 없으면 열린 쪽이 `window.opener` 로 이 창을 조작할 수 있다.
      a.rel = 'noopener noreferrer';
      a.textContent = this.#s.policy;
      holder.append(a);
      inner.append(holder);
    }

    const boxes = new Map<string, HTMLInputElement>();
    if (this.#detail) inner.append(this.#purposeList(boxes));
    inner.append(this.#buttons(boxes, regime));

    wrap.append(inner);
    this.#root.append(wrap);

    // 첫 버튼으로 초점을 옮긴다 — 키보드만 쓰는 이용자가 바로 선택할 수 있다.
    this.#root.querySelector('button')?.focus();
  }

  #purposeList(boxes: Map<string, HTMLInputElement>): HTMLElement {
    const ul = document.createElement('ul');
    ul.className = 'purposes';
    for (const p of this.#config.purposes) {
      const li = document.createElement('li');
      const lab = document.createElement('label');
      const input = document.createElement('input');
      input.type = 'checkbox';
      if (p.essential === true) {
        // **필수는 조작 자체를 막는다.** 끌 수 있는 것처럼 보이는데 안 꺼지면
        // 그게 더 나쁘다.
        input.checked = true;
        input.disabled = true;
      } else {
        boxes.set(p.id, input);
      }
      const span = document.createElement('span');
      span.textContent = labelOf(p, this.#locale);
      if (p.essential === true) {
        const note = document.createElement('span');
        note.className = 'fixed';
        note.textContent = ` (${this.#s.essential})`;
        span.append(note);
      }
      const desc = p.description?.[this.#locale];
      if (desc !== undefined) {
        const d = document.createElement('div');
        d.className = 'fixed';
        d.textContent = desc;
        span.append(d);
      }
      lab.append(input, span);
      li.append(lab);
      ul.append(li);
    }
    return ul;
  }

  #buttons(boxes: Map<string, HTMLInputElement>, regime: Regime): HTMLElement {
    const row = document.createElement('div');
    row.className = 'row';
    const mk = (text: string, ghost: boolean, fn: () => void): HTMLButtonElement => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = text;
      if (ghost) b.className = 'ghost';
      b.addEventListener('click', fn);
      return b;
    };

    if (this.#detail) {
      row.append(
        mk(this.#s.save, false, () => {
          const picks: Record<string, Choice> = {};
          for (const [id, input] of boxes) picks[id] = input.checked ? 'granted' : 'denied';
          this.#finish(picks);
        }),
      );
    } else {
      row.append(mk(this.#s.acceptAll, false, () => this.#finish(this.#every('granted'))));
    }

    // 🔴 **거부가 수락과 같은 깊이에 있어야 한다.** 거부를 한 단계 뒤로
    // 숨기면 「동의를 유도했다」가 된다. 규제 판단이 그 지점을 본다.
    row.append(mk(this.#s.rejectAll, true, () => this.#finish(this.#every('denied'))));

    if (!this.#detail) {
      row.append(
        mk(this.#s.customize, true, () => {
          this.#detail = true;
          this.show(regime);
        }),
      );
    }
    return row;
  }

  #every(c: Choice): Record<string, Choice> {
    const picks: Record<string, Choice> = {};
    for (const p of this.#config.purposes) {
      if (p.essential !== true) picks[p.id] = c;
    }
    return picks;
  }

  #finish(picks: Record<string, Choice>): void {
    this.#clear();
    this.#handlers.onDecide(picks);
  }

  /** 상시 설정 변경 진입점. **한 번 정한 뒤에도 바꿀 수 있어야 한다.** */
  showPreferencesButton(regime: Regime): void {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'fab ghost';
    // 글자는 화면에서 빼고 이름으로만 남긴다 — 화면 낭독기와 툴팁이 읽는다.
    b.setAttribute('aria-label', this.#s.prefs);
    b.title = this.#s.prefs;
    b.append(cookieIcon());
    b.addEventListener('click', () => {
      this.#detail = true;
      this.show(regime);
    });
    this.#root.append(b);
  }

  #clear(): void {
    for (const el of Array.from(this.#root.children)) {
      if (el.tagName !== 'STYLE') el.remove();
    }
  }

  destroy(): void {
    this.#host.remove();
  }
}
